import { useState } from 'react'
import { Stepper } from './Stepper'
import type { DishGoalCategory } from '@fellowship/shared'

interface Props {
  categories: DishGoalCategory[]
  onChange: (categories: DishGoalCategory[]) => void
}

const DEFAULT_CATEGORIES: { key: string; label: string }[] = [
  { key: 'main', label: 'Mains' },
  { key: 'salad', label: 'Salads' },
  { key: 'side', label: 'Sides' },
  { key: 'dessert', label: 'Desserts' },
  { key: 'drink', label: 'Drinks' },
]

const DEFAULT_GOAL = 4
const MAX_CATEGORIES = 12

function slugify(label: string): string {
  return label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'category'
}

export function DishGoalsFields({ categories, onChange }: Props) {
  const [customLabel, setCustomLabel] = useState('')

  const total = categories.reduce((sum, c) => sum + c.goal, 0)
  const atLimit = categories.length >= MAX_CATEGORIES

  function toggleDefault(defaultCat: { key: string; label: string }) {
    const isActive = categories.some((c) => c.key === defaultCat.key)
    if (isActive) {
      onChange(categories.filter((c) => c.key !== defaultCat.key))
    } else if (!atLimit) {
      onChange([...categories, { key: defaultCat.key, label: defaultCat.label, goal: DEFAULT_GOAL }])
    }
  }

  function updateGoal(key: string, goal: number) {
    onChange(categories.map((c) => (c.key === key ? { ...c, goal } : c)))
  }

  function removeCategory(key: string) {
    onChange(categories.filter((c) => c.key !== key))
  }

  function addCustom() {
    const label = customLabel.trim()
    if (!label || atLimit) return
    const base = slugify(label)
    let key = base
    let suffix = 2
    while (categories.some((c) => c.key === key)) {
      key = `${base}-${suffix}`
      suffix++
    }
    onChange([...categories, { key, label, goal: DEFAULT_GOAL }])
    setCustomLabel('')
  }

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {DEFAULT_CATEGORIES.map((cat) => {
          const isActive = categories.some((c) => c.key === cat.key)
          return (
            <button
              key={cat.key}
              type="button"
              onClick={() => toggleDefault(cat)}
              className={`chip h-[30px] text-[12.5px] px-[11px] cursor-pointer ${isActive ? 'chip--solid' : 'border-line-soft'}`}
            >
              {cat.label}
            </button>
          )
        })}
      </div>

      {categories.length > 0 && (
        <div className="grid gap-2.5 mb-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))' }}>
          {categories.map((cat) => (
            <div key={cat.key} className="p-3 rounded-xl bg-s2 border border-line-soft relative">
              <button
                type="button"
                onClick={() => removeCategory(cat.key)}
                aria-label={`Remove ${cat.label}`}
                className="absolute top-1 right-1.5 leading-none text-muted bg-transparent border-0 cursor-pointer text-[13px]"
              >
                ×
              </button>
              <div className="flex justify-center mb-2 px-3">
                <span className="text-[12px] font-semibold text-center">{cat.label}</span>
              </div>
              <Stepper value={cat.goal} onChange={(v) => updateGoal(cat.key, v)} />
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2 items-center">
        <input
          type="text"
          value={customLabel}
          onChange={(e) => setCustomLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              addCustom()
            }
          }}
          placeholder="Add your own, e.g. Potatoes"
          maxLength={40}
          disabled={atLimit}
          className="input flex-1"
        />
        <button
          type="button"
          onClick={addCustom}
          disabled={!customLabel.trim() || atLimit}
          className="h-[38px] px-3.5 rounded-full bg-s2 border border-line text-[12.5px] font-medium cursor-pointer disabled:opacity-50"
        >
          Add
        </button>
      </div>

      {categories.length > 0 && (
        <div className="mt-3 text-[11.5px] text-muted">
          Goal: <strong className="text-ink-2">{total} dish{total !== 1 ? 'es' : ''}</strong> across categories.
        </div>
      )}
    </div>
  )
}
